/**
 * InviteToLeadDialog Component
 * Modal for Lead Owners to invite a DSA to one of their marketplace leads
 *
 * Flow:
 * 1. Lead Owner browses DSA offers
 * 2. Clicks "Invite" on a DSA offer card
 * 3. This dialog opens - shows their active marketplace leads
 * 4. Lead Owner selects a lead and sends invite
 * 5. DSA receives notification and can accept/decline
 */

import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Divider } from 'primereact/divider';
import { dsaOfferService, getDsaLoanTypeConfig, formatCommission } from '../../../services/dsaOfferService';
import { marketplaceService, formatLoanAmount } from '../../../services/marketplaceService';

const InviteToLeadDialog = ({
    visible,
    onHide,
    dsaOffer,
    onSuccess
}) => {
    const toast = useRef(null);

    // State
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [myLeads, setMyLeads] = useState([]);
    const [selectedLeadId, setSelectedLeadId] = useState(null);
    const [inviteMessage, setInviteMessage] = useState('');
    const [expiresInDays, setExpiresInDays] = useState(7);

    // Fetch my marketplace leads
    useEffect(() => {
        if (visible && dsaOffer) {
            fetchMyLeads();
        }
    }, [visible, dsaOffer]);

    const fetchMyLeads = async () => {
        setLoading(true);
        try {
            const response = await marketplaceService.getMyListings({
                status: 'active',
                per_page: 100
            });

            if (response.success) {
                const data = response.data || {};
                const listings = data.data || data.listings || [];
                setMyLeads(listings);

                // Reset selection
                setSelectedLeadId(null);
            }
        } catch (error) {
            console.error('Failed to fetch leads:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load your marketplace leads'
            });
        } finally {
            setLoading(false);
        }
    };

    // Send invite
    const handleSendInvite = async () => {
        if (!selectedLeadId || !dsaOffer) return;

        setSending(true);
        try {
            const response = await dsaOfferService.sendInvite({
                marketplace_lead_id: selectedLeadId,
                dsa_offer_id: dsaOffer.id,
                invite_message: inviteMessage || null,
                expires_in_days: expiresInDays
            });

            if (response.success) {
                onSuccess && onSuccess();
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: response.message || 'Failed to send invite'
                });
            }
        } catch (error) {
            console.error('Failed to send invite:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to send invite'
            });
        } finally {
            setSending(false);
        }
    };

    // Expiry options
    const expiryOptions = [
        { label: '3 Days', value: 3 },
        { label: '7 Days', value: 7 },
        { label: '14 Days', value: 14 },
        { label: '30 Days', value: 30 }
    ];

    // Lead option template
    const leadOptionTemplate = (lead) => {
        if (!lead) return null;

        // Get borrower name - check direct fields first, then nested
        const borrowerName = lead.full_name ||
            (lead.first_name ? `${lead.first_name} ${lead.last_name || ''}`.trim() : null) ||
            lead.lead_snapshot?.borrower_name ||
            lead.lead_snapshot?.masked_name ||
            lead.borrower_preview?.name ||
            'Lead';

        // Get loan amount - check direct field first, then nested
        const loanAmount = lead.loan_amount_required ||
            lead.loan_amount ||
            lead.lead_snapshot?.loan_amount_required ||
            lead.lead_details?.loan_amount_required ||
            0;

        // Get product type - check direct field first, then nested
        const productType = lead.product_type ||
            lead.lead_snapshot?.product_type ||
            lead.lead_details?.product_type ||
            'personal_loan';

        // Get city/location
        const location = lead.city || lead.state || '';

        const loanConfig = getDsaLoanTypeConfig(productType);

        return (
            <div className="flex align-items-center gap-2 py-1">
                <div className="flex-1">
                    <div className="font-medium text-900 text-sm">{borrowerName}</div>
                    <div className="flex align-items-center gap-2 text-xs text-500">
                        <Tag value={loanConfig.label} severity="info" className="text-xs" />
                        <span className="text-green-600 font-medium">{formatLoanAmount(loanAmount)}</span>
                        {location && <span className="text-400">• {location}</span>}
                    </div>
                </div>
            </div>
        );
    };

    // Get DSA info
    const dsaName = dsaOffer?.posted_by?.display_name || dsaOffer?.posted_by?.full_name || 'DSA';
    const dsaPhoto = dsaOffer?.posted_by?.profile_photo_url_full || null;
    const loanTypeConfig = getDsaLoanTypeConfig(dsaOffer?.loan_type);

    // Dialog footer
    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                style={{ fontSize: '0.7rem' }}
                onClick={onHide}
                disabled={sending}
            />
            <Button
                label="Send Enquiry"
                icon="pi pi-send"
                style={{ fontSize: '0.7rem' }}
                onClick={handleSendInvite}
                loading={sending}
                disabled={!selectedLeadId || sending}
            />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                onHide={onHide}
                header={
                    <div className="flex align-items-center gap-2">
                        <i className="pi pi-send text-primary" style={{ fontSize: '0.8rem' }} />
                        <span style={{ fontSize: '0.85rem' }}>Send enquiry to payout for your lead</span>
                    </div>
                }
                footer={dialogFooter}
                style={{ width: '420px' }}
                breakpoints={{ '576px': '95vw' }}
                modal
                draggable={false}
                className="p-fluid compact-invite-lead-dialog"
            >
                {/* DSA Info Card */}
                <div className="surface-100 border-round p-2 mb-3">
                    <div className="text-500 text-xs mb-1">Sending enquiry to:</div>
                    <div className="flex align-items-center gap-2">
                        <Avatar
                            image={dsaPhoto}
                            icon={!dsaPhoto ? 'pi pi-user' : null}
                            size="normal"
                            shape="circle"
                            className="bg-primary-100"
                        />
                        <div className="flex-1">
                            <div className="font-bold text-900" style={{ fontSize: '0.8rem' }}>{dsaName}</div>
                            <div className="flex align-items-center gap-2 text-xs">
                                <Tag value={dsaOffer?.bank_name} severity="info" style={{ fontSize: '0.55rem', padding: '0.1rem 0.3rem' }} />
                                <Tag value={loanTypeConfig.label} style={{ fontSize: '0.55rem', padding: '0.1rem 0.3rem' }} />
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-green-600 font-bold" style={{ fontSize: '0.85rem' }}>
                                {formatCommission(dsaOffer?.commission_percentage)}
                            </div>
                            <div className="text-500" style={{ fontSize: '0.6rem' }}>Commission</div>
                        </div>
                    </div>
                </div>

                <Divider className="my-2" />

                {/* Select Lead */}
                <div className="mb-3">
                    <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.75rem' }}>
                        Select your lead <span className="text-red-500">*</span>
                    </label>
                    <p className="text-500 mb-2" style={{ fontSize: '0.6rem' }}>
                        Choose which lead to send this enquiry for
                    </p>

                    {loading ? (
                        <div className="flex flex-column gap-1">
                            <Skeleton height="2.5rem" />
                            <Skeleton height="2.5rem" />
                        </div>
                    ) : myLeads.length === 0 ? (
                        <Message
                            severity="warn"
                            className="w-full"
                            text="You don't have any active marketplace leads. Post a lead first to send enquiries."
                        />
                    ) : (
                        <Dropdown
                            value={selectedLeadId}
                            options={myLeads.map(lead => ({
                                label: lead.full_name ||
                                    (lead.first_name ? `${lead.first_name} ${lead.last_name || ''}`.trim() : null) ||
                                    lead.lead_snapshot?.borrower_name ||
                                    lead.lead_snapshot?.masked_name ||
                                    lead.borrower_preview?.name ||
                                    'Lead',
                                value: lead.id,
                                lead: lead
                            }))}
                            onChange={(e) => setSelectedLeadId(e.value)}
                            optionLabel="label"
                            placeholder="Select a lead..."
                            className="w-full"
                            style={{ fontSize: '0.7rem' }}
                            itemTemplate={(option) => leadOptionTemplate(option.lead)}
                            valueTemplate={(option) => option ? leadOptionTemplate(option.lead || myLeads.find(l => l.id === option.value)) : 'Select a lead...'}
                        />
                    )}
                </div>

                {/* Invite Message */}
                <div className="mb-3">
                    <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.75rem' }}>
                        Message (optional)
                    </label>
                    <InputTextarea
                        value={inviteMessage}
                        onChange={(e) => setInviteMessage(e.target.value)}
                        rows={2}
                        placeholder="Add a personal message to the DSA..."
                        maxLength={1000}
                        className="w-full"
                        style={{ fontSize: '0.7rem' }}
                    />
                    <div className="text-right text-500" style={{ fontSize: '0.6rem' }}>
                        {inviteMessage.length}/1000
                    </div>
                </div>

                {/* Expiry */}
                <div className="mb-3">
                    <label className="block text-900 font-medium mb-1" style={{ fontSize: '0.75rem' }}>
                        Enquiry expires in
                    </label>
                    <Dropdown
                        value={expiresInDays}
                        options={expiryOptions}
                        onChange={(e) => setExpiresInDays(e.value)}
                        className="w-full"
                        style={{ fontSize: '0.7rem' }}
                    />
                </div>

                {/* Info Message */}
                <Message
                    severity="info"
                    className="w-full"
                    style={{ fontSize: '0.65rem' }}
                    content={
                        <div style={{ fontSize: '0.65rem' }}>
                            <i className="pi pi-info-circle mr-2" style={{ fontSize: '0.6rem' }} />
                            When the DSA accepts, they will express interest in your lead and negotiation will begin.
                        </div>
                    }
                />

                <style>{`
                    .compact-invite-lead-dialog .p-dialog-header {
                        padding: 0.75rem 1rem !important;
                    }
                    .compact-invite-lead-dialog .p-dialog-content {
                        padding: 0.5rem 1rem !important;
                    }
                    .compact-invite-lead-dialog .p-dialog-footer {
                        padding: 0.5rem 1rem !important;
                    }
                    .compact-invite-lead-dialog .p-dropdown .p-dropdown-label {
                        font-size: 0.7rem !important;
                        padding: 0.4rem 0.5rem !important;
                    }
                    .compact-invite-lead-dialog .p-dropdown .p-dropdown-label.p-placeholder {
                        font-size: 0.7rem !important;
                    }
                    .compact-invite-lead-dialog .p-inputtextarea {
                        font-size: 0.7rem !important;
                    }
                `}</style>
            </Dialog>
        </>
    );
};

export default InviteToLeadDialog;
